/**
 * RID (Registry of Interpreters for the Deaf) Configuration
 * National certification requirements and logic
 */

// RID Certificate Types and their CEU requirements
export const RID_CERTIFICATE_TYPES = {
  'CI': {
    name: 'Certificate of Interpretation (CI)',
    totalRequired: 8.0,
    cycleYears: 4,
    requirements: {
      'Professional Studies': { min: 6.0, max: 8.0 },
      'General Studies': { min: 0.0, max: 2.0 },
      'PPO': { min: 1.0, max: 8.0 } // PPO is part of Professional Studies
    },
    description: '6.0-8.0 Professional Studies (including 1.0 PPO) + 0.0-2.0 General Studies'
  },
  
  'CT': {
    name: 'Certificate of Transliteration (CT)',
    totalRequired: 8.0,
    cycleYears: 4,
    requirements: {
      'Professional Studies': { min: 6.0, max: 8.0 },
      'General Studies': { min: 0.0, max: 2.0 },
      'PPO': { min: 1.0, max: 8.0 }
    },
    description: '6.0-8.0 Professional Studies (including 1.0 PPO) + 0.0-2.0 General Studies'
  },
  
  'CI/CT': {
    name: 'CI/CT (Both Certificates)',
    totalRequired: 8.0,
    cycleYears: 4,
    requirements: {
      'Professional Studies': { min: 6.0, max: 8.0 },
      'General Studies': { min: 0.0, max: 2.0 },
      'PPO': { min: 1.0, max: 8.0 }
    },
    description: '6.0-8.0 Professional Studies (including 1.0 PPO) + 0.0-2.0 General Studies'
  },
  
  'CDI': {
    name: 'Certified Deaf Interpreter (CDI)',
    totalRequired: 8.0,
    cycleYears: 4,
    requirements: {
      'Professional Studies': { min: 6.0, max: 8.0 },
      'General Studies': { min: 0.0, max: 2.0 },
      'PPO': { min: 1.0, max: 8.0 }
    },
    description: '6.0-8.0 Professional Studies (including 1.0 PPO) + 0.0-2.0 General Studies'
  },
  
  'SC:L': {
    name: 'Specialist Certificate: Legal (SC:L)',
    totalRequired: 8.0,
    cycleYears: 4,
    requirements: {
      'Professional Studies': { min: 6.0, max: 8.0 },
      'General Studies': { min: 0.0, max: 2.0 },
      'PPO': { min: 1.0, max: 8.0 }
    },
    description: '6.0-8.0 Professional Studies (including 1.0 PPO) + 0.0-2.0 General Studies'
  },
  
  'NIC': {
    name: 'National Interpreter Certification (NIC)',
    totalRequired: 8.0,
    cycleYears: 4,
    requirements: {
      'Professional Studies': { min: 6.0, max: 8.0 },
      'General Studies': { min: 0.0, max: 2.0 },
      'PPO': { min: 1.0, max: 8.0 }
    },
    description: '6.0-8.0 Professional Studies (including 1.0 PPO) + 0.0-2.0 General Studies'
  },
  
  'NIC Advanced': {
    name: 'NIC Advanced',
    totalRequired: 8.0,
    cycleYears: 4,
    requirements: {
      'Professional Studies': { min: 6.0, max: 8.0 },
      'General Studies': { min: 0.0, max: 2.0 },
      'PPO': { min: 1.0, max: 8.0 }
    },
    description: '6.0-8.0 Professional Studies (including 1.0 PPO) + 0.0-2.0 General Studies'
  },
  
  'NIC Master': {
    name: 'NIC Master',
    totalRequired: 8.0,
    cycleYears: 4,
    requirements: {
      'Professional Studies': { min: 6.0, max: 8.0 },
      'General Studies': { min: 0.0, max: 2.0 },
      'PPO': { min: 1.0, max: 8.0 }
    },
    description: '6.0-8.0 Professional Studies (including 1.0 PPO) + 0.0-2.0 General Studies'
  }
};

// RID-specific CEU categories and subcategories
export const RID_CATEGORIES = {
  'Professional Studies': {
    label: 'Professional Studies',
    description: 'Interpreting-specific training (6.0-8.0 CEUs required)',
    subcategories: [
      { value: 'Educational', label: 'Educational', description: 'General interpreting education and training' },
      { value: 'Legal', label: 'Legal', description: 'Court and legal interpreting' },
      { value: 'Medical', label: 'Medical', description: 'Healthcare interpreting' },
      { value: 'Mental Health', label: 'Mental Health', description: 'Therapeutic and counseling settings' },
      { value: 'Educational Interpreting', label: 'Educational Interpreting', description: 'K-12 and post-secondary education' },
      { value: 'PPO', label: 'Power, Privilege & Oppression (PPO)', description: 'Required PPO training - counts toward both PS and PPO requirement' },
      { value: 'Business', label: 'Business', description: 'Corporate and business interpreting' },
      { value: 'Technology', label: 'Technology', description: 'VRI, apps, and digital interpreting tools' },
      { value: 'Ethics', label: 'Ethics', description: 'Professional ethics and standards' },
      { value: 'Linguistics', label: 'Linguistics', description: 'ASL linguistics and language studies' },
      { value: 'Blind Low Vision', label: 'Blind/Low Vision', description: 'DeafBlind and low vision interpreting' },
      { value: 'Tactile', label: 'Tactile', description: 'Tactile interpreting techniques' },
      { value: 'Pro-Tactile', label: 'Pro-Tactile', description: 'Pro-Tactile ASL and communication' }
    ]
  },
  
  'General Studies': {
    label: 'General Studies',
    description: 'Non-interpreting related education (0.0-2.0 CEUs maximum)',
    subcategories: [
      { value: 'College Courses', label: 'College Credit Courses', description: 'Non-interpreting college coursework (e.g., MBA, general business, psychology)' },
      { value: 'Personal Development', label: 'Personal Development', description: 'General leadership, communication, and soft skills not specific to interpreting' },
      { value: 'Business Skills', label: 'General Business Skills', description: 'Business and entrepreneurship skills not related to interpreting business' },
      { value: 'Technology Skills', label: 'General Technology Skills', description: 'General technology and digital literacy not specific to interpreting tools' },
      { value: 'Health Wellness', label: 'Health & Wellness', description: 'General health, fitness, and wellness training' },
      { value: 'Creative Arts', label: 'Creative Arts', description: 'Art, music, writing, and other creative pursuits' },
      { value: 'Academic Subjects', label: 'Academic Subjects', description: 'General academic subjects (math, science, history, etc.)' },
      { value: 'Life Skills', label: 'Life Skills', description: 'General life management and personal skills' },
      { value: 'Professional Certification', label: 'Professional Certification', description: 'Non-interpreting professional certifications and training' }
    ]
  }
};

// RID-specific rules and validations
export const RID_RULES = {
  // PPO requirement
  PPO_REQUIREMENT: 'Minimum 1.0 CEU must be in Power, Privilege, and Oppression (PPO) topics.',
  
  // Professional Studies requirement
  PROFESSIONAL_STUDIES_MIN: 'Minimum 6.0 CEUs must be in Professional Studies.',
  
  // General Studies limitation
  GENERAL_STUDIES_MAX: 'Maximum 2.0 CEUs may be in General Studies.',
  
  // 4-year cycle
  CYCLE_LENGTH: 'RID certification requires 8.0 CEUs every 4 years.',
  
  // CEU submission timing
  SUBMISSION_TIMING: 'CEUs must be submitted to RID before your certification expiration date.'
};

/**
 * Get RID requirements for a specific certificate type
 * @param {string} certificateType - RID certificate type
 * @returns {Object} Requirements object
 */
export function getRIDRequirements(certificateType) {
  return RID_CERTIFICATE_TYPES[certificateType] || RID_CERTIFICATE_TYPES['CI/CT'];
}

/**
 * Calculate RID CEU progress for a user
 * @param {Array} ceus - Array of CEU records
 * @param {string} certificateType - RID certificate type
 * @returns {Object} Progress calculation
 */
export function calculateRIDProgress(ceus, certificateType) {
  const requirements = getRIDRequirements(certificateType);
  const progress = {
    total: 0,
    professionalStudies: 0,
    generalStudies: 0,
    ppo: 0,
    byCategory: {},
    requirements: requirements.requirements,
    totalRequired: requirements.totalRequired,
    cycleYears: requirements.cycleYears,
    isComplete: false,
    warnings: [],
    certificateType
  };

  // Initialize category totals
  Object.keys(requirements.requirements).forEach(category => {
    progress.byCategory[category] = 0;
  });

  // Calculate totals by category
  ceus.forEach(ceu => {
    const hours = parseFloat(ceu.ceuHours) || 0;
    progress.total += hours;
    
    if (ceu.category === 'Professional Studies') {
      progress.professionalStudies += hours;
      progress.byCategory['Professional Studies'] += hours;
      
      // Check for PPO subcategory
      if (ceu.subCategory === 'PPO') {
        progress.ppo += hours;
        progress.byCategory['PPO'] += hours;
      }
    } else if (ceu.category === 'General Studies') {
      progress.generalStudies += hours;
      progress.byCategory['General Studies'] += hours;
    }
  });

  // Check if requirements are met
  let requirementsMet = true;
  
  // Check Professional Studies minimum
  if (progress.professionalStudies < requirements.requirements['Professional Studies'].min) {
    requirementsMet = false;
    const needed = requirements.requirements['Professional Studies'].min - progress.professionalStudies;
    progress.warnings.push(`Need ${needed.toFixed(1)} more Professional Studies CEUs`);
  }
  
  // Check PPO minimum
  if (progress.ppo < requirements.requirements['PPO'].min) {
    requirementsMet = false;
    const needed = requirements.requirements['PPO'].min - progress.ppo;
    progress.warnings.push(`Need ${needed.toFixed(1)} more PPO (Power, Privilege & Oppression) CEUs`);
  }
  
  // Check General Studies maximum
  if (progress.generalStudies > requirements.requirements['General Studies'].max) {
    progress.warnings.push(`Exceeded maximum General Studies: ${progress.generalStudies.toFixed(1)}/${requirements.requirements['General Studies'].max} CEUs`);
  }
  
  // Check total requirement
  if (progress.total < requirements.totalRequired) {
    requirementsMet = false;
    progress.warnings.push(`Need ${(requirements.totalRequired - progress.total).toFixed(1)} more CEUs total`);
  }

  progress.isComplete = requirementsMet;

  return progress;
}

/**
 * Validate a CEU against RID rules
 * @param {Object} ceu - CEU record
 * @param {Array} existingCeus - Existing CEU records
 * @param {string} certificateType - RID certificate type
 * @returns {Object} Validation result
 */
export function validateRIDCEU(ceu, existingCeus, certificateType) {
  const validation = {
    isValid: true,
    errors: [],
    warnings: []
  };

  const requirements = getRIDRequirements(certificateType);
  
  // Check if category is valid for RID
  if (!['Professional Studies', 'General Studies'].includes(ceu.category)) {
    validation.isValid = false;
    validation.errors.push(`${ceu.category} is not a valid RID category. Use 'Professional Studies' or 'General Studies'.`);
  }

  // Check General Studies limit
  if (ceu.category === 'General Studies') {
    const currentGeneral = existingCeus
      .filter(existing => existing.id !== ceu.id && existing.category === 'General Studies')
      .reduce((sum, existing) => sum + parseFloat(existing.ceuHours), 0);
    
    const newTotal = currentGeneral + parseFloat(ceu.ceuHours);
    const maxGeneral = requirements.requirements['General Studies'].max;
    
    if (newTotal > maxGeneral) {
      validation.warnings.push(`This would exceed the maximum General Studies CEUs allowed (${maxGeneral}). Current: ${currentGeneral}, Adding: ${ceu.ceuHours}`);
    }
  }

  return validation;
}

const ridConfig = {
  RID_CERTIFICATE_TYPES,
  RID_CATEGORIES,
  RID_RULES,
  getRIDRequirements,
  calculateRIDProgress,
  validateRIDCEU
};

export default ridConfig;
